import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Heart } from "lucide-react";
import { useShop } from "@/store";
import { M, P, ART, byId } from "@/data/catalogue";
import ProductCard, { Colorway } from "@/components/ProductCard";
import { Reveal, MaskedLines } from "@/components/motion";
import { NotFound } from "@/pages/Misc";

const TRUST_ICONS = {
  hand: <svg viewBox="0 0 24 24"><path d="M12 21s-7-4.5-7-10a4 4 0 0 1 7-2.5A4 4 0 0 1 19 11c0 5.5-7 10-7 10z" /><path d="M9 11h6" /></svg>,
  box: <svg viewBox="0 0 24 24"><path d="M3 8l9-4 9 4v9l-9 4-9-4z" /><path d="M3 8l9 4 9-4M12 12v9" /></svg>,
  shield: <svg viewBox="0 0 24 24"><path d="M12 3l8 3v6c0 5-3.5 8-8 9-4.5-1-8-4-8-9V6z" /><path d="m9 12 2 2 4-4" /></svg>,
};

export default function Product() {
  const { id } = useParams();
  const p = byId(id);
  const { t, L, money, wish, toggleWish, addCart, setDrawer } = useShop();
  const [g, setG] = useState(null);
  const [zoom, setZoom] = useState(false);
  const [q, setQ] = useState(1);

  if (!p) return <NotFound />;

  const a = ART[p.artisan];
  const pd = (k) => t(`pdp.${k}`);
  const gal = [p.img, ...(p.id === "amaya" ? ["amaya_detail", "amaya_top"] : ["hands", "tassel"]), "lifestyle"];
  const cur = g && gal.includes(g) ? g : gal[0];
  const onWish = wish.includes(p.id);

  const zoomMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    e.currentTarget.style.transformOrigin = `${((e.clientX - r.left) / r.width) * 100}% ${((e.clientY - r.top) / r.height) * 100}%`;
  };

  return (
    <>
      <div className="wrap crumbs" data-testid="pdp-crumbs">
        <Link to="/shop">{t("nav.shop")}</Link> / {p.n}
      </div>
      <div className="wrap pdp">
        <Reveal>
          <div className="gallery">
            <div className="main">
              <img
                src={M[cur]}
                alt={`${p.n} Wayuu bag`}
                data-testid="pdp-main-image"
                className={zoom ? "zoom" : ""}
                onClick={(e) => { zoomMove(e); setZoom(!zoom); }}
                onMouseMove={zoom ? zoomMove : undefined}
              />
            </div>
            <div className="thumbs">
              {gal.map((gi, i) => (
                <button key={gi} className={cur === gi ? "on" : ""} data-testid={`pdp-thumb-${i}`} aria-label={`View ${i + 1}`} onClick={() => { setG(gi); setZoom(false); }}>
                  <img src={M[gi]} alt="" loading="lazy" />
                </button>
              ))}
            </div>
            <div className="acc" style={{ marginTop: 34 }}>
              <details open>
                <summary data-testid="pdp-acc-details">{pd("details")}</summary>
                <div className="body">
                  <p>{L(p).d}</p>
                  <table style={{ marginTop: 14 }}>
                    <tbody>
                      <tr><td>{pd("pattern")}</td><td>{p.pattern}</td></tr>
                      <tr><td>{pd("by")}</td><td><Link to="/artisans" style={{ color: "var(--terra)" }}>{a.n}</Link> · {a.place}</td></tr>
                      <tr><td>{pd("days")}</td><td>{p.days}</td></tr>
                      <tr><td>{pd("colourway")}</td><td><Colorway p={p} /></td></tr>
                    </tbody>
                  </table>
                </div>
              </details>
              <details><summary data-testid="pdp-acc-dims">{pd("dims")}</summary><div className="body">{pd(`dims${p.size}`)}</div></details>
              <details><summary data-testid="pdp-acc-origin">{pd("origin")}</summary><div className="body">{pd("originT")}</div></details>
              <details><summary data-testid="pdp-acc-care">{pd("care")}</summary><div className="body">{pd("careT")}</div></details>
              <details><summary data-testid="pdp-acc-shipping">{pd("shipping")}</summary><div className="body">{pd("shipT")}</div></details>
            </div>
          </div>
        </Reveal>
        <Reveal delay={0.12}>
          <div className="buy">
            <div className="eyebrow">{p.one ? pd("one") : pd("lim")}</div>
            <MaskedLines as="h1" lines={[`The ${p.n} bag`]} delay={0.15} testid="pdp-title" />
            <div className="price" data-testid="pdp-price">{money(p.price)}</div>
            <div className="tax">{pd("tax")}</div>
            <p className="lede" style={{ marginTop: 18, fontSize: 16 }}>{L(p).d}</p>
            <div className="qty" aria-label={t("cart.qty")}>
              <button data-testid="pdp-qty-minus" disabled={p.one || q <= 1} onClick={() => setQ(Math.max(1, q - 1))}>−</button>
              <span data-testid="pdp-qty">{p.one ? 1 : q}</span>
              <button data-testid="pdp-qty-plus" disabled={p.one || q >= 5} onClick={() => setQ(Math.min(5, q + 1))}>+</button>
            </div>
            <div className="buyrow">
              <button className="btn primary" data-testid="pdp-add-to-cart" style={{ flex: 1, justifyContent: "center" }} onClick={() => { addCart(p.id, q); setDrawer({ type: "cart" }); }}>
                {pd("add")}
              </button>
              <button
                className={`icon wish${onWish ? " on" : ""}`}
                data-testid="pdp-wishlist-toggle"
                aria-label="Wishlist"
                aria-pressed={onWish}
                style={{ width: 48, height: 48, border: "1px solid var(--line)", borderRadius: 2 }}
                onClick={() => toggleWish(p.id)}
              >
                <Heart />
              </button>
            </div>
            <div className="trust">
              <div>{TRUST_ICONS.hand} {pd("hand")}</div>
              <div>{TRUST_ICONS.box} {pd("ship")}</div>
              <div>{TRUST_ICONS.shield} {pd("ret")}</div>
            </div>
            <div className="cert" data-testid="pdp-certificate">
              <div className="qr" aria-hidden="true" />
              <div>
                <b>{pd("cert")}</b>
                <code data-testid="pdp-cert-code">{p.code}</code><br />
                <small>{pd("certs")}</small>
                <div className="vbtn">
                  <Link className="btn ghost" data-testid="pdp-verify-link" to="/impact#verify">{pd("verify")} →</Link>
                </div>
              </div>
            </div>
            <p className="note" style={{ marginTop: 18 }}>{pd("note")}</p>
          </div>
        </Reveal>
      </div>

      <section className="sec alt" style={{ marginTop: 60 }}>
        <div className="wrap split">
          <Reveal><div className="im"><img src={M.rancheria} alt="Wayuu weavers under a palm shelter" loading="lazy" /></div></Reveal>
          <Reveal delay={0.12}>
            <div className="eyebrow">{pd("by")}</div>
            <h2 style={{ fontSize: "clamp(30px,3.6vw,46px)" }}>{a.n}</h2>
            <p className="lede" style={{ fontStyle: "italic", fontFamily: "var(--display)", fontSize: 24 }}>“{L(a).q}”</p>
            <p style={{ marginTop: 14, color: "var(--fg-2)" }}>{L(a).b}</p>
            <div style={{ marginTop: 22 }}>
              <Link className="btn ghost" to="/artisans">{t("nav.artisans")} →</Link>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="sec">
        <div className="wrap">
          <Reveal className="center"><div className="eyebrow">{pd("also")}</div></Reveal>
          <div className="grid g4" style={{ marginTop: 26 }}>
            {P.filter((x) => x.id !== p.id).slice(0, 4).map((x, i) => (
              <Reveal key={x.id} delay={i * 0.07}><ProductCard p={x} /></Reveal>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}
